import type {
  LogItemType,
  LogAttributesType,
  OwnersLinkType,
  Change,
} from "@/types/common/logs";
import { Badge, Icon } from "@elaniintc/goes-design-system";
import { FORM_ASSIGNMENTS_STATUS } from "@/types/entities/formality-request";
import { AssociateLogRow } from "./user-log-row";
import type { ActionColors, Status } from "./logs.types";
import { getChanges, statusColor, statusTraducer } from "./logs.helpers";

export const RegistryAssociateAddedBanner = ({ log }: { log: LogItemType }) => {
  const userOperations = [
    "users_linked",
    "associate_added",
    "owners_linked",
    "associate_updated",
    "users_unlinked",
    "owners_unlinked",
    "associate_removed",
    "associate_deleted",
  ];
  if (!userOperations.includes(log.action)) {
    return null;
  }

  const applicant = log.properties?.applicant;
  const roleName =
    log.properties?.role_name ||
    (log.properties?.attributes?.role_name as string | undefined);
  const isOwner = Boolean(
    log.properties?.is_owner || log.properties?.attributes?.is_owner,
  );
  return (
    <div className="flex items-center p-2 gap-2 mb-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
      <AssociateLogRow
        firstName={applicant?.first_name}
        lastName={applicant?.last_name}
        email={applicant?.email || ""}
        roleName={roleName}
        isOwner={isOwner}
        action={log.action}
      />
    </div>
  );
};

export const RegistryLinkedUsers = ({ log }: { log: LogItemType }) => {
  const users: Array<OwnersLinkType> = [
    ...(log?.properties?.owners_linked || []),
    ...(log?.properties?.owners_skipped || []),
    ...(log?.properties?.users_linked || []),
    ...(log?.properties?.unlinked || []),
  ];

  if (!users || users.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col mb-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
      {users?.map((user) => (
        <AssociateLogRow
          key={user.applicant_id}
          action={user.action}
          isOwner={user.is_owner}
          email={user.email}
          roleName={user.role_name}
          reason={user.reason}
          className="border-b border-neutral-quiet p-2 last:border-0"
        />
      ))}
    </div>
  );
};

export const Banner = ({ change }: { change: Change }) => {
  const formatValue = (
    val: string | string[] | boolean | number | null | undefined,
  ) => {
    if (Array.isArray(val)) return val.join(", ");
    if (typeof val === "object" && val !== null) return JSON.stringify(val);
    return String(val ?? "");
  };

  const renderTextChange = () => (
    <>
      <h3 className="line-through text-on-disabled">
        {formatValue(change.previous)}
      </h3>
      <h3 className="text-body">{formatValue(change.current)}</h3>
    </>
  );

  const renderBadgeChange = (
    prev: React.ReactNode,
    next: React.ReactNode,
    prevVariant: ActionColors,
    nextVariant: ActionColors,
  ) => (
    <div className="flex items-center gap-2">
      <span className="text-sm text-body">De</span>
      <Badge size="xs" variant={prevVariant}>
        {prev}
      </Badge>
      <span className="text-sm text-body">a</span>
      <Badge size="xs" variant={nextVariant}>
        {next}
      </Badge>
    </div>
  );

  const renderTagsChange = () =>
    renderBadgeChange(
      <>
        {typeof change.previous === "string" && change.previous}
        <Icon type="fas" name="edit" className="text-warning-quiet text-xs" />
      </>,
      <>
        {typeof change.current === "string" && change.current}
        <Icon type="fas" name="check" className="text-success-quiet text-xs" />
      </>,
      "warning",
      "success",
    );

  const renderStatusChange = () =>
    renderBadgeChange(
      statusTraducer(change.previous as keyof typeof FORM_ASSIGNMENTS_STATUS),
      statusTraducer(change.current as keyof typeof FORM_ASSIGNMENTS_STATUS),
      statusColor(change.previous as string),
      statusColor(change.current as string),
    );

  const renderActiveChange = () =>
    renderBadgeChange(
      change.previous ? "Activa" : "Inactiva",
      change.current ? "Activa" : "Inactiva",
      change.previous ? "success" : "neutral",
      change.current ? "success" : "neutral",
    );

  const renderFileChange = () => (
    <div className="flex items-center gap-2">
      <Badge size="xs" variant="disabled">
        <span className="text-sm text-on-disabled">{change.previous}</span>
        <Icon type="far" name="file" />
      </Badge>
      <Icon type="far" name="arrow-right" />
      <Badge size="xs" variant="neutral">
        <span className="text-sm">{change.current}</span>
        <Icon type="far" name="file" />
      </Badge>
    </div>
  );

  const renderPermissionsChange = () => {
    const prev = Array.isArray(change.previous) ? change.previous : [];
    const curr = Array.isArray(change.current) ? change.current : [];

    const added = curr.filter((p) => !prev.includes(p));
    const removed = prev.filter((p) => !curr.includes(p));

    return (
      <div className="flex flex-col gap-2 w-full">
        {added.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-sm text-body-quiet">Agregados</span>
            <div className="flex flex-wrap gap-1">
              {added.map((p) => (
                <Badge key={`add-${p}`} size="xs" variant="success">
                  {p}
                </Badge>
              ))}
            </div>
          </div>
        )}
        {removed.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="text-sm text-body-quiet">Eliminados</span>
            <div className="flex flex-wrap gap-1">
              {removed.map((p) => (
                <Badge key={`rem-${p}`} size="xs" variant="danger">
                  {p}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col min-h-11 mb-2 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
      {["name", "description"].includes(change.name) && renderTextChange()}
      {change.name === "tags" && renderTagsChange()}
      {change.name === "status" && renderStatusChange()}
      {change.name === "active" && renderActiveChange()}
      {change.name === "file_name" && renderFileChange()}
      {change.name === "permissions" && renderPermissionsChange()}
    </div>
  );
};

export const BannerChanges = ({ log }: { log: LogItemType }) => {
  const changes = getChanges(
    log.properties?.old as LogAttributesType,
    log.properties?.attributes as LogAttributesType,
  );

  const statusChange = changes.find((c) => c.name === "status");
  const isArchived = statusChange?.current === "INACTIVE";
  const isUnarchived =
    statusChange?.previous === "INACTIVE" &&
    statusChange?.current !== "INACTIVE";

  if ((isArchived || isUnarchived) && statusChange) {
    return (
      <div className="flex flex-col min-h-11 mb-2 p-2 gap-2 self-stretch rounded-lg border border-neutral-quiet bg-neutral-object-accent-silent">
        <div className="flex items-center gap-2">
          <span className="text-sm text-body">De</span>
          <Badge
            size="xs"
            variant={statusColor(statusChange.previous as string)}
          >
            {statusTraducer(statusChange.previous as Status)}
          </Badge>
          <span className="text-sm text-body">a</span>
          <Badge
            size="xs"
            variant={statusColor(statusChange.current as string)}
          >
            {statusTraducer(statusChange.current as Status)}
          </Badge>
        </div>

        {log.properties?.attributes?.reason && (
          <div className="flex flex-col gap-1 mt-1">
            <div className="flex items-center gap-2">
              <Icon
                type="fas"
                name="comment"
                className={isArchived ? "text-warning" : "text-success"}
              />
              <span
                className={`text-sm font-semibold ${isArchived ? "text-warning" : "text-success"}`}
              >
                {isArchived
                  ? "Motivo de la archivación:"
                  : "Motivo de la reactivación:"}
              </span>
            </div>
            <span className="text-sm text-body">
              {log.properties.attributes.reason}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {changes.map((changeItem) => (
        <Banner key={changeItem.key} change={changeItem} />
      ))}
    </>
  );
};
